/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { INextSessionProps } from './types';
import { sessionSelectors } from '../../../../../app/resource/session/session.selectors';
import { useHistory } from 'react-router-dom';
import { agendaItemSelectors } from '../../../../../app/resource/agenda-item/agenda-item.selectors';
import { useDispatch } from 'react-redux';
import {
  convertAndFormatDateToUserTimeZone,
  isNextSessionWithinTheNextTwelveHours,
  isSessionToday,
} from '../../../../../utils/date.utils';
import { meetingSimpleSelectors } from '../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { agendaItemsPageSelectors } from '../../../../../app/page/page-storage/agenda-items/agenda-items.selectors';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import {
  joinMeeting,
  startMeetingSimpleSession,
} from '../../../../../app/page/page-storage/meetings-simple/meetings-simple.actions';
import { meetingsSimplePageSelectors } from '../../../../../app/page/page-storage/meetings-simple/meetings-simple.selectors';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import moment from 'moment';
import { ISession } from '../../../../../app/resource/session/session.types';
import { openDialog, closeDialog } from '../../../../../app/dialog/dialog.actions';
import { isMemberAMeetingAdmin, isMemberPartOfMeeting } from '../../../../../utils/user.utils';

export const NextSession: FunctionComponent<INextSessionProps> = ({ nextSessionId, lastSessionId, meetingId }) => {
  const lastSession = useSelectWithParams(sessionSelectors.selectResourceById, lastSessionId ?? '');
  const nextSession = useSelectWithParams(sessionSelectors.selectResourceById, nextSessionId ?? '');

  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId ? meetingId : '');
  const liveSessions = useSelect(meetingsSimplePageSelectors.selectLiveMeetings);

  const teamColor = useMemo(() => meeting?.color, [meeting]);
  const dispatch = useDispatch();
  const userId = useSelect(authSelectors.selectLoggedUser) || '';
  const user = useSelectWithParams(userSelectors.selectResourceById, userId);
  const [showLoader, setshowLoader] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    // check if there's an open session for this meeting
    const openSession = (liveSessions.find(
      (liveMeeting) => liveMeeting.meeting?.id === meetingId,
    ) as unknown) as ISession;
    if (openSession) {
      const sessionStartedAt = moment.utc(openSession?.startedDate);
      const toDate = moment.utc();
      // It is running if it started and not ended yet.
      // This should always return true since it's the same condition used to get the liveSessions within the API in the first place
      const isRunning = openSession && toDate.isAfter(sessionStartedAt) && !openSession?.finishedDate;
      setIsRunning(isRunning);
    } else {
      setIsRunning(false);
    }
  }, [liveSessions, meetingId]);

  const goToLiveMeeting = useCallback(() => {
    dispatch(closeDialog('confirmation'));

    const sessionLive = (liveSessions.find(
      (liveMeeting) => liveMeeting.meeting?.id === meetingId,
    ) as unknown) as ISession;
    setshowLoader(true);
    if (isRunning && sessionLive) {
      dispatch(joinMeeting(sessionLive));
    } else {
      dispatch(startMeetingSimpleSession(meetingId));
    }
  }, [dispatch, isRunning, liveSessions, meetingId]);

  const agendaItemIds = useSelect(agendaItemsPageSelectors.selectAgendaItems);
  const agendaItems = useSelectWithParams(
    agendaItemSelectors.selectResourcesById,
    agendaItemIds[meetingId] ? agendaItemIds[meetingId] : [],
  );

  const filteredAgendaItems = agendaItems.filter((ai) => ai?.checked === '1');

  const userSessionTimezoneTime =
    nextSession && user
      ? convertAndFormatDateToUserTimeZone(nextSession.sessionDate, user.timezone)
      : '(no upcoming meeting)';

  const sessionIsToday =
    lastSession && nextSession && user ? isSessionToday(lastSession.sessionDate, nextSession.sessionDate) : false;

  const checkIfUserIsAdminBeforeStartSession = (cb: any) => {
    if (user && meeting && !isMemberAMeetingAdmin(user, meeting)) {
      dispatch(
        openDialog('confirmation', {
          title: 'Meeting leaders',
          message: 'You are not a meeting leader. Do you want to continue anyway?',
          onConfirm: () => cb(true),
          onDeny: () => cb(false),
        }),
      );
    } else {
      cb(true);
    }
  };

  const checkIfSessionIsToday = useCallback(
    (cb) => {
      if (sessionIsToday) {
        cb(true);
      } else {
        dispatch(
          openDialog('confirmation', {
            title: 'Start meeting',
            message: 'There is not scheduled meeting for today. Do you want to continue anyway?',
            onConfirm: () => cb(true),
            onDeny: () => cb(false),
          }),
        );
      }
    },
    [dispatch, sessionIsToday],
  );

  const isMeetingMember = user && meeting && isMemberPartOfMeeting(user, meeting);

  const startOrJoinMeeting = () => {
    if (isRunning) {
      goToLiveMeeting();
    } else {
      checkIfSessionIsToday((confirmedStart: boolean) => {
        if (confirmedStart) {
          checkIfUserIsAdminBeforeStartSession((confirmed: boolean) => {
            if (confirmed) {
              goToLiveMeeting();
            }
          });
        }
      });
    }
  };

  return (
    <div className="col-6">
      <div className="panel-body pt-0 pr-0">
        <div className="border-radius-1 lightGray-bg p-2" style={{ minHeight: '330px' }}>
          <div className="panel py-1 px-0 mb-1 no-border flex flex-column aic jcc" style={{ height: '234px' }}>
            <div className="big-avatar-80 flex aic jcc f-3 my-1 mx-2 white bold" style={{ backgroundColor: teamColor }}>
              {meeting?.duration}&apos;
            </div>
            <div className="aic jcc flex flex-column">
              <h2 className="bold mt-2">Next Meeting</h2>
              <p
                className={
                  nextSession && isNextSessionWithinTheNextTwelveHours(nextSession.sessionDate)
                    ? 'session-is-today'
                    : ''
                }
              >
                {' '}
                {nextSession ? userSessionTimezoneTime : '(no upcoming meeting)'}
              </p>
            </div>
          </div>
          {isMeetingMember && nextSessionId && meeting && filteredAgendaItems.length > 0 && !showLoader && (
            <a className="btn btn-lg mt-2 flex aic jcc" style={{ cursor: 'pointer' }} onClick={startOrJoinMeeting}>
              <i className="icon2-play f-1 mr-1"></i> {isRunning ? 'Join Meeting' : 'Start Meeting'}
            </a>
          )}
          {isMeetingMember && nextSessionId && meeting && filteredAgendaItems.length === 0 && !showLoader && (
            <a className="btn btn-lg mt-2 flex aic jcc" style={{ backgroundColor: 'gray', pointerEvents: 'none' }}>
              <i className="icon2-play f-1 mr-1"></i> {isRunning ? 'Join Meeting' : 'Start Meeting'}
            </a>
          )}
          {isMeetingMember && nextSessionId && meeting && showLoader && (
            <a className="btn btn-lg mt-2 flex aic jcc" style={{ pointerEvents: 'none' }}>
              <span className="sr-only">Loading...</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
